import { get, put, post } from './commonFetch';
import { anyToken } from '../constants';
import {
  TripGroupView,
  AccountLineageGraphResponse,
  TripGroupAndAncestorsView,
  TripGroupViewSettings,
  CreateTripGroupRequest,
} from '../types/tripGroup';

type CreateReturn = { created: boolean; data: TripGroupAndAncestorsView };
export const createTripGroup = (
  tripGroup: CreateTripGroupRequest,
): Promise<CreateReturn> => {
  return post('/api/createTripGroup', tripGroup);
};

export const getTripGroup = (
  tripGroupId: string,
): Promise<TripGroupAndAncestorsView> => {
  return get(`/api/tripGroups/${tripGroupId}`);
};

export const updateTripGroup = (
  tripGroupId: string,
  tripGroupSettings: TripGroupViewSettings,
): Promise<TripGroupAndAncestorsView> => {
  return put(`/api/tripGroups/${tripGroupId}`, tripGroupSettings);
};

export type OverridenValue = boolean | 'True' | 'False' | 'Any';
export type SearchTripGroupsArgs = {
  name?: string;
  isOverridden?: OverridenValue;
  messageConfigName?: string;
  attributes?: Record<string, string>;
  includeHidden?: boolean;
  sort?: string;
};

export const getTripGroupAttributes = (): Promise<AccountLineageGraphResponse> => {
  return get('/api/accounts/tripGroupAttributes');
};

export const tgSearchParamObjectToQS = (
  tgSearchParamsObject: SearchTripGroupsArgs,
): Record<string, string> => {
  const {
    name,
    isOverridden = 'Any',
    messageConfigName,
    attributes,
    sort,
    includeHidden,
  } = tgSearchParamsObject;
  // attributes you do not want to filter should be excluded from both the attributes param and their respective named param
  //   those with emptyString values are intentionally left in here and excluded later as appropriate
  const nonAnyAttributeNames: string[] =
    (attributes &&
      Object.keys(attributes).filter(
        attribute => attributes[attribute] !== anyToken,
      )) ||
    [];

  const outgoingQueryParams: Record<string, string> = {};

  // attributes = a coma delimited list of all non-anyToken-valued attribute names
  //   including any with empty-string values
  nonAnyAttributeNames?.length &&
    (outgoingQueryParams.attributes = nonAnyAttributeNames.join(','));

  // exclude name if it's undefined or empty string
  // we're opting to just make tripGroups whose name is empty string not searchable by name
  name && (outgoingQueryParams.name = name);

  // exclude includeHidden if false
  includeHidden && (outgoingQueryParams.includeHidden = 'true');

  // don't pass email config selection if it's empty or the any token value
  messageConfigName &&
    messageConfigName?.toString() !== 'Any' &&
    (outgoingQueryParams.messageConfigName = messageConfigName.toString());

  // don't pass isOverridden if it's empty or the any token value (b/c they could use "Any" as an attribute value
  outgoingQueryParams.overridden =
    isOverridden?.toString() !== anyToken ? isOverridden.toString() : 'Any';

  // add an explicit key-value pair for each attribute passed with a non-anyToken value
  //   and exclude any that have empty-string as the value
  attributes &&
    nonAnyAttributeNames.forEach(attribute => {
      if (attributes[attribute]) {
        outgoingQueryParams[attribute] = attributes[attribute];
      }
    });

  // exclude sort if not present
  sort && (outgoingQueryParams.sort = sort);

  return outgoingQueryParams;
};

type SearchTripGroupsResponse = {
  tripGroupViewList: TripGroupView[];
  count: number;
};

export const searchTripGroups = (
  params: SearchTripGroupsArgs,
): Promise<SearchTripGroupsResponse> => {
  return get('/api/tripGroups', tgSearchParamObjectToQS(params));
};
